'use client';

import { useState } from "react";
import Link from "next/link";
import { useAuth } from "../../lib/auth";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

export default function Login() {
  const pathname = usePathname();
  const currentLocale = pathname.match(/^\/([a-z]{2}-[A-Z]{2})/)?.[1] || 'zh-CN';
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const t = useTranslations('login');

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsLoading(true);
    setError("");

    try {
      console.log('Calling login function...');
      const result = await login(formData.email, formData.password);
      console.log('Login result:', result);
      if (result.success) {
        // 根据用户角色重定向
        if (result.user?.role === 'admin') {
          console.log('Redirecting to admin...');
          // 直接重定向，不需要延迟
          window.location.href = `/${currentLocale}/admin`;
        } else {
          window.location.href = `/${currentLocale}`;
        }
      } else {
        setError(result.error || "登录失败");
        console.log('Login failed:', result.error);
      }
    } catch (err) {
      setError("服务器错误");
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-600">{t('title')}</h1>
          <p className="text-black mt-2">{t('subtitle')}</p>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1">{t('email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('email')}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-1">{t('password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('password')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                {t('remember')}
              </label>
            </div>
            <div className="text-sm">
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                {t('forgotPassword')}
              </a>
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            {isLoading ? t('loading') : t('login')}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            {t('noAccount')} <Link href={`/${currentLocale}/register`} className="font-medium text-blue-600 hover:text-blue-500">{t('register')}</Link>
          </p>
        </div>
        
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">或使用以下方式登录</span>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <button type="button" className="inline-flex justify-center items-center p-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-500 hover:bg-gray-50">
              <span className="text-lg">📱</span>
            </button>
            <button type="button" className="inline-flex justify-center items-center p-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-500 hover:bg-gray-50">
              <span className="text-lg">📘</span>
            </button>
            <button type="button" className="inline-flex justify-center items-center p-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-500 hover:bg-gray-50">
              <span className="text-lg">📧</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}